import * as React from 'react';
import cx from 'classnames';
import { useFlags } from 'gatsby-plugin-launchdarkly';

import { Background } from './components/Background/Background';
import { Header } from './components/Header/Header';
import { Aside } from './components/Aside/Aside';
import { Footer } from './components/Footer/Footer';
import { FooterWide } from './components/Footer/FooterWide';
import { BackgroundLight } from '../../components/BackgroundLight/BackgroundLight';
import { JSONLD } from '../../components/JSONLD/JSONLD';
import { ConsultationCtaMini } from '../../components/ConsultationCtaMini/ConsultationCtaMini';
import { LayoutContext } from '../../context/LayoutContext';
import { routes } from '../../config/routes';
import { StickyMobileHeader } from './components/StickyMobileHeader/StickyMobileHeader';
import { useScrollTo } from '../../utils/useScrollTo';
import { useSiteMetadata } from '../../utils/useSiteMetadata';
import { EASTER_LOGOS } from './components/CompanyLogo/CompanyLogo';

import * as cs from './PageLayout.module.css';
import './styles/base.css';
import './styles/code.css';
import './styles/theme.css';
import './styles/iubenda.css';

interface Props {
  children: React.ReactNode;
  themeVariables?: object;
  themeMeta?: CustomTypes.ThemeMeta;
  withBg?: boolean;
  page: string;
  title?: string;
  description?: string;
  location: Window['location'];
  article?: boolean;
  dateISO?: string;
  authors?: readonly Queries.authorFieldsFragment[];
  cover?: string | null;
  socialCard?: string | null;
  withBgImage?: boolean;
  isBgFullSize?: boolean | null;
  endDateISO?: string;
  address?: string;
  url?: string;
  tags?: CustomTypes.Tags;
  showEmailNewsletterForm?: boolean;
  showUpcomingEvent?: boolean;
  overflow?: React.ReactNode;
  eventType?: 'talk' | 'podcast';
}

type WindowWithDataLayer = Window & {
  dataLayer: Record<string, any>[];
};

declare const window: WindowWithDataLayer;

export const PageLayout = ({
  children,
  themeVariables,
  themeMeta,
  withBg,
  isBgFullSize,
  page,
  title,
  description,
  cover,
  socialCard,
  location,
  article,
  dateISO,
  authors,
  withBgImage,
  endDateISO,
  address,
  url,
  tags,
  showEmailNewsletterForm = true,
  showUpcomingEvent = true,
  overflow,
  eventType,
}: Props) => {
  const { calCom } = useSiteMetadata();

  const scrollToElement = useScrollTo({
    contactUs: location.hash === routes.contactUs,
    to: location && location.hash,
  });

  const launchDarklyFlags = useFlags();

  React.useEffect(() => {
    if (
      !!launchDarklyFlags &&
      !!Object.keys(launchDarklyFlags).length &&
      typeof window !== 'undefined' &&
      typeof window.dataLayer !== 'undefined'
    ) {
      const flagsToString = Object.keys(launchDarklyFlags)
        .filter((key) => launchDarklyFlags[key] === true)
        .join();
      window.dataLayer.push({
        event: 'set-launch-darkly-flags',
        launchDarklyFlags: flagsToString,
      });
    }
  }, [launchDarklyFlags]);

  React.useEffect(() => {
    if (location && location.hash) {
      // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
      scrollToElement();
    }
  }, [location, scrollToElement]);

  const mobileHeaderRef = React.useRef(null);
  const desktopHeaderRef = React.useRef(null);

  const errorPage = page === 'error';
  const policyPage = page === 'privacy' || page === 'cookies';
  const mainPage = page === 'main';
  const landingPage = page === 'landing';
  const postPage = page === 'post';
  const contactUsPage = page === 'contact-us';
  const hideContactUs =
    errorPage ||
    policyPage ||
    (landingPage && !!launchDarklyFlags && launchDarklyFlags.lpconversion);

  return (
    <>
      {withBg && <Background page={page} />}

      <StickyMobileHeader
        mainPage={mainPage}
        withPageBg={withBg}
        withBgImage={withBgImage}
        isBgFullSize={isBgFullSize}
        innerRef={mobileHeaderRef}
        themeVariables={themeVariables}
        hideContactUs={hideContactUs}
      />

      <div
        className={cx(cs.root, {
          [cs.errorPage]: errorPage,
        })}
        style={themeVariables}
        data-with-theme
      >
        <div className={cs.layout}>
          <Header
            forceUseTheme={errorPage}
            errorPage={errorPage}
            mainPage={mainPage}
            withBgImage={withBgImage}
            isBgFullSize={isBgFullSize}
            withPageBg={withBg}
            ref={desktopHeaderRef}
            themeMeta={themeMeta}
            showUpcomingEvent={showUpcomingEvent}
          />

          <aside className={cs.aside}>
            <div className={cs.fixed}>
              <Aside forceUseTheme={errorPage} hideContactUs={hideContactUs} />
            </div>
          </aside>

          {overflow}

          <main className={cs.main}>
            {withBg &&
              !mainPage &&
              (['services', 'products', 'chronicles'].includes(page || '') ? (
                <>
                  <BackgroundLight
                    size={62}
                    shadowSize={200}
                    left="-16.6%"
                    top="1900px"
                    color="#a8fe69"
                  />

                  <BackgroundLight
                    size={14}
                    shadowSize={100}
                    right="5%"
                    top="3400px"
                    color="#58fbf1"
                  />

                  <BackgroundLight
                    size={56}
                    shadowSize={200}
                    right="-9.7%"
                    top="3800px"
                    color="#afa2fe"
                  />
                </>
              ) : (
                <>
                  <BackgroundLight
                    size={122}
                    shadowSize={200}
                    left="-34.3%"
                    top="3600px"
                    color="#afa2fe"
                  />

                  <BackgroundLight
                    size={14}
                    shadowSize={100}
                    right="-11%"
                    top="5200px"
                    color="#58fbf1"
                  />
                </>
              ))}

            <LayoutContext.Provider
              // @ts-expect-error TS(2322): Type 'MutableRefObject<null>' is not assignable to... Remove this comment to see the full error message
              value={{ mobileHeaderRef, desktopHeaderRef }}
            >
              {children}
            </LayoutContext.Provider>
          </main>

          {!errorPage && !policyPage && (
            <>
              {contactUsPage ? (
                <FooterWide
                  page={page}
                  showEmailNewsletterForm={showEmailNewsletterForm}
                />
              ) : (
                <>
                  {!postPage && (
                    <ConsultationCtaMini
                      calComLink={calCom}
                      openEventName="book-call-open-widget-cta"
                      scrollEventName="book-call-view-widget-cta"
                      id="contact-us"
                      className={cx(
                        cs.consultationCtaMini,
                        mainPage && cs.consultationCtaMiniMain,
                      )}
                      text="Evil Martians transform growth-stage startups into unicorns, build developer tools, and create open source products. Hire us to design and build your product"
                      textLg
                      needEmail
                    />
                  )}
                  <Footer page={page} />
                </>
              )}
            </>
          )}
        </div>
      </div>

      {Object.values(EASTER_LOGOS).map((logo) => (
        <link rel="preload" as="image" href={logo} key={logo} />
      ))}

      <JSONLD
        title={title}
        description={description}
        pathname={location.pathname}
        article={article}
        dateISO={dateISO}
        authors={authors}
        cover={cover}
        socialCard={socialCard}
        page={page}
        endDateISO={endDateISO}
        address={address}
        url={url}
        tags={tags}
        eventType={eventType}
      />
    </>
  );
};
